import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
    useMemo,
  } from "react";
  import { Box, CircularProgress, Typography } from "@material-ui/core";
import { ShoppingCart as ShoppingCartIcon } from "@material-ui/icons";
  import { toast } from "react-toastify";
  import api from "../../services/api";
  import * as pharmaApi from "../../services/pharmaApi";
  import { AuthContext } from "../../context/Auth/AuthContext";
import { useSalesPanel } from "../../context/SalesPanel/SalesPanelContext";
  import toastError from "../../errors/toastError";


  // Hooks
  import useCartPersistence from "../../hooks/useCartPersistence";
  import useCustomerStats from "../../hooks/useCustomerStats";
  import usePdvKeyboard from "../../hooks/usePdvKeyboard";
  import usePdvSuggestions from "../../hooks/usePdvSuggestions";
  import useCustomerSuggestions from "../../hooks/useCustomerSuggestions";

  // Styles
  import { usePdvStyles } from "./styles/pdvStyles";

  // Components
  import PdvHeader from "./components/PdvHeader";
  import PdvSearchBar from "./components/PdvSearchBar";
  import PdvCustomerSummary from "./components/PdvCustomerSummary";
  import PdvProductList from "./components/PdvProductList";
  import PdvSalesDrawer from "./components/PdvSalesDrawer";
  import PdvSuggestionsPanel from "./components/PdvSuggestionsPanel";
  import PdvSuggestionsNotification from "./components/PdvSuggestionsNotification";
  import PdvFinalizeConfirmModal from "./components/PdvFinalizeConfirmModal";

  // Modals
  import CustomerIdentificationModal from "../CustomerIdentificationModal";
  import CustomerProfileModal from "../CustomerProfileModal";
  import QuoteSearchModal from "../QuoteSearchModal";
  import PdvKeyboardShortcuts from "../PdvKeyboardShortcuts";
  import PdvDiscountModal from "../PdvDiscountModal";
  import PdvSendSuggestionsModal from "../PdvSendSuggestionsModal";

  const TicketPdvPanel = ({ ticketId, ticketUuid, contactId, contactPhone, contactName, contact, contactLoading, onContactUpdated, containerRef: externalContainerRef }) => {
    const classes = usePdvStyles();
    const { user } = useContext(AuthContext);
    const { registerPdvActions, updateCartInfo, stores, setStores, selectedStore, setSelectedStore } = useSalesPanel();
    const searchInputRef = useRef(null);
    const cartListRef = useRef(null);
    const productListRef = useRef(null);
    const internalContainerRef = useRef(null);
    const containerRef = externalContainerRef || internalContainerRef;

    // State - Main
    const [searchQuery, setSearchQuery] = useState("");
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    // stores and selectedStore now come from useSalesPanel context
    const [settings, setSettings] = useState(null);

    // State - Cart & UI
    const [salesDrawerOpen, setSalesDrawerOpen] = useState(false);
    const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
    const [selectedCartIndex, setSelectedCartIndex] = useState(-1);
    const [paymentMethod, setPaymentMethod] = useState("dinheiro");
    const [deliveryType, setDeliveryType] = useState("delivery");
    const [notes, setNotes] = useState("");
    const [internalNotes, setInternalNotes] = useState("");
    const [changeAmount, setChangeAmount] = useState(0);

    // State - Operations
    const [isSending, setIsSending] = useState(false);
    const [isSavingQuote, setIsSavingQuote] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [sendingProductId, setSendingProductId] = useState(null);

    // State - Customer
    const [customerData, setCustomerData] = useState(null);
    const [pharmaCustomer, setPharmaCustomer] = useState(null); // Customer from PharmaFlow with addresses
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [customerModalOpen, setCustomerModalOpen] = useState(false);

    // State - Modals
    const [quoteSearchOpen, setQuoteSearchOpen] = useState(false);
    const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false);
    const [discountModalOpen, setDiscountModalOpen] = useState(false);
    const [discountTarget, setDiscountTarget] = useState(null);
    const [customerProfileOpen, setCustomerProfileOpen] = useState(false);

    // State - Quote recovery
    const [recoveredQuoteId, setRecoveredQuoteId] = useState(null);

    // State - Suggestions modal
    const [suggestionsModalOpen, setSuggestionsModalOpen] = useState(false);
    const [sendingSuggestions, setSendingSuggestions] = useState(false);
    const [pendingFinalize, setPendingFinalize] = useState(false);

    // State - Finalize confirmation modal
    const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);

    // Cart persistence hook - uses UUID for storage key
    const {
      cart,
      setCart,
      addToCart: addToCartPersisted,
      updateQuantity: updateQuantityPersisted,
      removeItem: removeItemPersisted,
      clearCart: clearCartPersisted,
      applyDiscount: applyDiscountPersisted,
    } = useCartPersistence(ticketUuid);

    // Customer stats hook
    const { stats: customerStats, loading: statsLoading } = useCustomerStats(contactId);

    // Suggestions hook - product-based
    const {
      sameFormula,
      relatedProducts,
      clearSuggestions,
      refetch: loadSuggestions,
    } = usePdvSuggestions(null, selectedStore?.id);

    // Customer suggestions hook - based on purchase history
    const {
      suggestions: customerSuggestions,
      hasSuggestions: hasCustomerSuggestions,
      dismissSuggestion: dismissCustomerSuggestion,
      dismissAll: dismissAllCustomerSuggestions,
      loading: customerSuggestionsLoading,
    } = useCustomerSuggestions(pharmaCustomer?.id, {
      storeId: selectedStore?.id,
      limit: 8,
      enabled: !!pharmaCustomer?.id && settings?.showCustomerSuggestions !== false,
    });

    // Combine suggestions for keyboard navigation
    const suggestions = useMemo(() => {
      const combined = [];
      if (sameFormula?.products?.length > 0) {
        combined.push(...sameFormula.products);
      }
      if (relatedProducts?.products?.length > 0) {
        combined.push(...relatedProducts.products);
      }
      return combined;
    }, [sameFormula, relatedProducts]);

    // Calculate totals
    const totals = useMemo(() => {
      const cartItems = cart || [];
      const subtotal = cartItems.reduce((sum, item) => {
        const originalPrice = Number(item.originalPrice) || Number(item.unitPrice) || 0;
        return sum + originalPrice * Number(item.quantity);
      }, 0);

      const itemsDiscount = cartItems.reduce((sum, item) => {
        const discount = Number(item.discount) || 0;
        return sum + discount * Number(item.quantity);
      }, 0);

      const total = subtotal - itemsDiscount;

      return {
        subtotal,
        itemsDiscount,
        total,
        itemCount: cartItems.reduce((sum, item) => sum + Number(item.quantity), 0),
      };
    }, [cart]);

    // Check if customer is identified (has required fields from customerData OR pharmaCustomer)
    const isCustomerIdentified = useMemo(() => {
      // Check customerData (from Whaticket Contact)
      if (customerData) {
        const { cpf, cep, street, streetNumber, neighborhood } = customerData;
        if (cpf && cep && street && streetNumber && neighborhood) return true;
      }
      // Fallback: check pharmaCustomer with addresses (from PharmaFlow)
      if (pharmaCustomer && pharmaCustomer.cpf && pharmaCustomer.addresses?.length > 0) {
        const addr = pharmaCustomer.addresses.find(a => a.isDefault) || pharmaCustomer.addresses[0];
        if (addr.zipcode && addr.street && addr.number && addr.neighborhood) return true;
      }
      return false;
    }, [customerData, pharmaCustomer]);
    // Fetch stores from PharmaFlow
    useEffect(() => {
      const fetchStores = async () => {
        try {
          const data = await pharmaApi.getStores();
          if (data && data.length > 0) {
            setStores(data);
            setSelectedStore(data[0]);
          }
        } catch (err) {
          console.error("Error fetching stores from PharmaFlow:", err);
        }
      };
      fetchStores();
    }, []);

    // Fetch settings from PharmaFlow
    useEffect(() => {
      const fetchSettings = async () => {
        try {
          const data = await pharmaApi.getSettings();
          setSettings(data);
        } catch (err) {
          console.error("Error fetching settings from PharmaFlow:", err);
        }
      };
      fetchSettings();
    }, []);

    // Reset search state when ticket changes
    useEffect(() => {
      setSearchQuery("");
      setProducts([]);
      setSelectedProductIndex(-1);
      setSelectedCartIndex(-1);
      setSalesDrawerOpen(false);
    }, [ticketUuid]);

    // Fetch customer data from contact
    useEffect(() => {
      const fetchCustomerData = async () => {
        if (!contactId) return;
        try {
          const { data } = await api.get(`/contacts/${contactId}`);
          if (data) {
            setCustomerData({
              name: data.name || contactName,
              cpf: data.cpf,
              email: data.email,
              cep: data.zipCode || data.cep,
              street: data.street,
              streetNumber: data.streetNumber,
              complement: data.complement,
              neighborhood: data.neighborhood,
              city: data.city,
              state: data.state,
            });
          }
        } catch (err) {
          console.error("Error fetching customer data:", err);
        }
      };
      fetchCustomerData();
    }, [contactId, contactName]);

    // Fetch PharmaFlow customer with addresses - reusable function
    const fetchPharmaCustomer = useCallback(async () => {
      if (!contactId && !contactPhone) return;

      try {
        let customer = null;

        // Try by contactId first
        if (contactId) {
          try {
            customer = await pharmaApi.getCustomerByContactId(contactId);
          } catch (e) {
            // Not found by contactId
          }
        }

        // Fallback to whatsapp
        if (!customer && contactPhone) {
          try {
            const cleanNumber = contactPhone.replace(/\D/g, "");
            customer = await pharmaApi.getCustomerByWhatsapp(cleanNumber);
          } catch (e) {
            // Not found by whatsapp
          }
        }

        if (customer) {
          setPharmaCustomer(customer);
          // Set default address if available
          if (customer.addresses && customer.addresses.length > 0) {
            const defaultAddr = customer.addresses.find(a => a.isDefault) || customer.addresses[0];
            setSelectedAddressId(defaultAddr.id);
          }
        }
      } catch (err) {
        console.error("Error fetching PharmaFlow customer:", err);
      }
    }, [contactId, contactPhone]);

    // Initial fetch of PharmaFlow customer
    useEffect(() => {
      fetchPharmaCustomer();
    }, [fetchPharmaCustomer]);

    // Search products from PharmaFlow
    const searchProductsFn = useCallback(
      async (query) => {
        if (!query || query.length < 2 || !selectedStore) {
          setProducts([]);
          return;
        }

        setSearching(true);
        try {
          // Build search options with filter settings from salesPanel defaults
          const searchOptions = { limit: 30 };

          // Apply active filter from settings (salesPanelDefaultActiveFilter)
          // Values: "" = all, "true" = active only, "false" = inactive only
          if (settings?.salesPanelDefaultActiveFilter === "true") {
            searchOptions.isActive = true;
          } else if (settings?.salesPanelDefaultActiveFilter === "false") {
            searchOptions.isActive = false;
          }
          // If "" or undefined, don't add filter (show all)

          // Apply stock filter from settings (salesPanelDefaultStockFilter)
          // Values: "" = all, "with" = with stock, "without" = without stock
          // Backend expects stockFilter as string: "with" or "without"
          if (settings?.salesPanelDefaultStockFilter && settings.salesPanelDefaultStockFilter !== "") {
            searchOptions.stockFilter = settings.salesPanelDefaultStockFilter;
          }
          // If "" or undefined, don't add filter (show all)

          const data = await pharmaApi.searchProducts(query, selectedStore.id, searchOptions);
          const productList = data?.products || data || [];
          setProducts(productList);
          setSelectedProductIndex(productList.length > 0 ? 0 : -1);
        } catch (err) {
          console.error("Error searching products in PharmaFlow:", err);
          setProducts([]);
        } finally {
          setSearching(false);
        }
      },
      [selectedStore, settings]
    );

    // Debounce search
    useEffect(() => {
      const timer = setTimeout(() => {
        searchProductsFn(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }, [searchQuery, searchProductsFn]);

    // Add to cart
    const handleAddToCart = useCallback(
      (product, quantity = 1) => {
        addToCartPersisted({
          ...product,
          quantity,
          originalPrice: product.currentPrice || product.price,
          unitPrice: product.currentPrice || product.price,
          discount: 0,
          discountPercent: 0,
        });

        // Load suggestions for related products
        if (settings?.suggestRelatedProducts || settings?.showSameFormulaProducts) {
          loadSuggestions(product);
        }

        const qtdText = quantity > 1 ? ` (${quantity}x)` : '';
        toast.success(`${product.name}${qtdText} adicionado ao carrinho`);
      },
      [addToCartPersisted, loadSuggestions, settings]
    );

    // Send product to customer (image + text)
    const handleSendProduct = useCallback(
      async (product) => {
        if (!ticketUuid) {
          toast.error("Nenhum ticket selecionado");
          return;
        }

        setSendingProductId(product.id);
        try {
          const formatCurrency = (value) =>
            new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(value || 0);

          // Build product message
          const price = product.currentPrice || product.price || 0;
          let caption = `*${product.name}*\n`;
          // Filter out "Sim"/"Não" values (data import issue) - only show real active principles
          const activePrinciple = product.activePrinciple?.trim();
          if (activePrinciple && !["sim", "não", "nao"].includes(activePrinciple.toLowerCase())) {
            caption += `${activePrinciple}\n`;
          }
          caption += `\nPreco: ${formatCurrency(price)}`;
          if (product.barcode) {
            caption += `\nCodigo: ${product.barcode}`;
          }

          // If product has image, download and send as media with caption
          if (product.imageUrl) {
            try {
              // Fetch image and convert to blob - handle CORS by using no-cors mode first
              const imageUrl = product.imageUrl;
              let blob;

              try {
                // Try normal fetch first (works for same-origin or CORS-enabled URLs)
                const response = await fetch(imageUrl, { mode: 'cors' });
                if (!response.ok) throw new Error('Failed to fetch image');
                blob = await response.blob();
              } catch (corsErr) {
                // If CORS fails, try using an image element to load and convert via canvas
                console.log("CORS fetch failed, trying canvas approach:", corsErr);
                const img = new Image();
                img.crossOrigin = "anonymous";

                await new Promise((resolve, reject) => {
                  img.onload = resolve;
                  img.onerror = () => reject(new Error('Image load failed'));
                  img.src = imageUrl;
                });

                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
              }

              if (!blob || blob.size === 0) {
                throw new Error('Empty blob');
              }

              const filename = `produto_${product.barcode || product.id}.jpg`;
              const file = new File([blob], filename, { type: blob.type || "image/jpeg" });

              const formData = new FormData();
              formData.append("medias", file);
              formData.append("body", caption);
              formData.append("fromMe", true);

              await api.post(`/messages/${ticketUuid}`, formData);
              toast.success("Produto enviado com imagem!");
            } catch (imgErr) {
              console.error("Error downloading product image, sending text only:", imgErr);
              // Fallback: send text only
              await api.post(`/messages/${ticketUuid}`, {
                body: caption,
                fromMe: true,
              });
              toast.success("Produto enviado (sem imagem)");
            }
          } else {
            // No image, send text only
            await api.post(`/messages/${ticketUuid}`, {
              body: caption,
              fromMe: true,
            });
            toast.success("Produto enviado!");
          }
        } catch (err) {
          console.error("Error sending product:", err);
          toast.error("Erro ao enviar produto");
        } finally {
          setSendingProductId(null);
        }
      },
      [ticketUuid]
    );

    // Update quantity - PdvCartItem passes (index, newQuantity)
    const handleUpdateQuantity = useCallback(
      (index, newQuantity) => {
        const item = cart[index];
        if (item) {
          updateQuantityPersisted(item.id, newQuantity);
        }
      },
      [cart, updateQuantityPersisted]
    );

    // Remove item - PdvCartItem passes (index)
    const handleRemoveItem = useCallback(
      (index) => {
        const item = cart[index];
        if (item) {
          removeItemPersisted(item.id);
        }
      },
      [cart, removeItemPersisted]
    );

    // Apply discount - receives (index, discountValue, discountPercent) but hook wants (productId, discountPercent)
    const handleApplyDiscount = useCallback(
      (index, discountValue, discountPercent) => {
        const item = cart[index];
        if (item) {
          applyDiscountPersisted(item.id, discountPercent);
        }
      },
      [cart, applyDiscountPersisted]
    );

    // Open discount modal
    const handleOpenDiscountModal = useCallback((index) => {
      setDiscountTarget(index);
      setDiscountModalOpen(true);
    }, []);

    // Clear cart
    const handleClearCart = useCallback(() => {
      clearCartPersisted();
      setRecoveredQuoteId(null);
      clearSuggestions();
    }, [clearCartPersisted, clearSuggestions]);

    // Save quote and send to customer
    const handleSaveQuote = useCallback(async () => {
      if (!cart || cart.length === 0) {
        toast.warning("Carrinho vazio!");
        return;
      }

      setIsSavingQuote(true);
      try {
        // Create quote via PharmaFlow API
        // Convert ticketId to number (it comes as string from URL params)
        const numericTicketIdForQuote = ticketId ? parseInt(ticketId, 10) : undefined;
        const quoteData = {
          storeId: selectedStore?.id,
          whaticketTicketId: numericTicketIdForQuote && !isNaN(numericTicketIdForQuote) ? numericTicketIdForQuote : undefined,
          whaticketContactId: contactId,
          customerName: customerData?.name || contactName,
          customerPhone: contactPhone,
          customerCpf: customerData?.cpf,
          items: cart.map((item) => ({
            productId: item.id,
            productSnapshot: JSON.stringify({
              id: item.id,
              name: item.name,
              ean: item.ean,
              activePrinciple: item.activePrinciple,
            }),
            quantity: item.quantity,
            originalPrice: item.originalPrice || item.unitPrice,
            unitPrice: item.originalPrice || item.unitPrice,
            discount: item.discount || 0,
            discountPercent: item.discountPercent || 0,
            total: ((item.originalPrice || item.unitPrice) - (item.discount || 0)) * item.quantity,
          })),
          subtotal: totals.subtotal,
          discountTotal: totals.itemsDiscount,
          total: totals.total,
          notes,
        };

        const quote = await pharmaApi.createQuote(quoteData);

        // Send quote to customer via PharmaFlow API
        if (ticketUuid) {
          try {
            const sendResult = await pharmaApi.sendQuoteToCustomer(quote.id, ticketId);

            if (sendResult.message) {
              await api.post(`/messages/${ticketUuid}`, {
                body: sendResult.message,
                fromMe: true,
              });
            }
          } catch (sendErr) {
            console.warn("Could not send quote message:", sendErr);
          }
        }

        toast.success(`Orçamento #${quote.quoteNumber} salvo e enviado!`);
        handleClearCart();
        setSearchQuery("");
        setSalesDrawerOpen(false);
      } catch (err) {
        console.error("Error saving quote:", err);
        toastError(err);
      } finally {
        setIsSavingQuote(false);
      }
    }, [
      cart,
      selectedStore,
      ticketId,
      ticketUuid,
      contactId,
      customerData,
      contactName,
      contactPhone,
      totals,
      notes,
      handleClearCart,
    ]);

    // Copy quote to clipboard
    const handleCopyQuote = useCallback(async () => {
      if (!cart || cart.length === 0) {
        toast.warning("Carrinho vazio!");
        return;
      }

      setIsCopying(true);
      try {
        // First create a temporary quote to get formatted message via PharmaFlow
        const quoteData = {
          storeId: selectedStore?.id,
          customerName: customerData?.name || contactName,
          customerPhone: contactPhone,
          customerCpf: customerData?.cpf,
          items: cart.map((item) => ({
            productId: item.id,
            productSnapshot: JSON.stringify({
              id: item.id,
              name: item.name,
              ean: item.ean,
              activePrinciple: item.activePrinciple,
            }),
            quantity: item.quantity,
            originalPrice: item.originalPrice || item.unitPrice,
            unitPrice: item.originalPrice || item.unitPrice,
            discount: item.discount || 0,
            discountPercent: item.discountPercent || 0,
            total: ((item.originalPrice || item.unitPrice) - (item.discount || 0)) * item.quantity,
          })),
          subtotal: totals.subtotal,
          discountTotal: totals.itemsDiscount,
          total: totals.total,
          notes,
          status: "draft", // Mark as draft so it doesn't count as sent
        };

        const quote = await pharmaApi.createQuote(quoteData);
        const result = await pharmaApi.formatQuoteMessage(quote.id);

        if (result.message) {
          await navigator.clipboard.writeText(result.message);
          toast.success("Orçamento copiado para a área de transferência!");
        }
      } catch (err) {
        console.error("Error copying quote:", err);
        toastError(err);
      } finally {
        setIsCopying(false);
      }
    }, [cart, customerData, contactName, contactPhone, totals, selectedStore, notes]);

    // Finalize sale - internal function that actually creates the order
    const handleFinalizeSaleInternal = useCallback(async () => {
      if (!cart || cart.length === 0) {
        toast.warning("Carrinho vazio!");
        return;
      }

      // Check if contact is still loading
      if (contactLoading) {
        toast.info("Carregando dados do contato. Aguarde um momento...");
        return;
      }

      // Check if contact is loaded before opening identification modal
      if (!contactId && !contact?.id) {
        toast.error("Contato não carregado. Recarregue a página e tente novamente.");
        console.error("[TicketPdvPanel] Contact not loaded:", { contactId, contact, contactLoading });
        return;
      }

      if (!isCustomerIdentified) {
        toast.warning("Preencha os dados do cliente antes de finalizar!");
        setCustomerModalOpen(true);
        return;
      }

      setIsSending(true);
      setFinalizeConfirmOpen(false);
      try {
        // Get the selected address for delivery fee calculation
        const addressForFee = pharmaCustomer?.addresses?.length > 0
          ? pharmaCustomer.addresses.find(a => a.id === selectedAddressId) || pharmaCustomer.addresses[0]
          : customerData;

        // Calculate delivery fee based on neighborhood/city or use base fee
        let deliveryFeeValue = 0;
        if (deliveryType === 'delivery' && addressForFee) {
          // Try to find fee by neighborhood first
          const neighborhoodFee = settings?.deliveryFeeByNeighborhood?.find(
            f => f.neighborhood?.toLowerCase() === addressForFee.neighborhood?.toLowerCase()
          );

          if (neighborhoodFee) {
            deliveryFeeValue = Number(neighborhoodFee.fee) || 0;
          } else {
            // Try to find fee by city
            const cityFee = settings?.deliveryFeeByCity?.find(
              f => f.city?.toLowerCase() === addressForFee.city?.toLowerCase()
            );

            if (cityFee) {
              deliveryFeeValue = Number(cityFee.fee) || 0;
            } else {
              // Use base fee as fallback
              deliveryFeeValue = Number(settings?.deliveryBaseFee) || 0;
            }
          }
        }

        // Map payment method to backend enum values
        const paymentMethodMap = {
          'dinheiro': 'cash',
          'money': 'cash',
          'pix': 'pix',
          'credit': 'credit_card',
          'credit_card': 'credit_card',
          'debit': 'debit_card',
          'debit_card': 'debit_card',
          'boleto': 'bank_slip',
          'bank_slip': 'bank_slip',
        };
        const mappedPaymentMethod = paymentMethodMap[paymentMethod] || 'cash';

        // Create order via PharmaFlow API
        // ticketId is the numeric ID from the ticket object
        const orderData = {
          storeId: selectedStore?.id,
          whaticketTicketId: ticketId || undefined,
          whaticketContactId: contactId,
          customerPhone: contactPhone,
          customerName: customerData?.name,
          customerCpf: customerData?.cpf,
          items: cart.map((item) => ({
            productId: item.id,
            productSnapshot: JSON.stringify({
              id: item.id,
              name: item.name,
              ean: item.ean,
              activePrinciple: item.activePrinciple,
            }),
            quantity: item.quantity,
            originalPrice: item.originalPrice || item.unitPrice,
            unitPrice: item.originalPrice || item.unitPrice,
            discount: item.discount || 0,
            discountPercent: item.discountPercent || 0,
          })),
          paymentMethod: mappedPaymentMethod,
          deliveryType,
          deliveryFee: deliveryFeeValue,
          notes: notes || undefined,
          internalNotes: internalNotes || undefined,
          recoveredQuoteId: recoveredQuoteId || undefined,
        };

        // Add change amount for cash payments
        if (changeAmount > 0 && mappedPaymentMethod === 'cash') {
          orderData.changeAmount = changeAmount;
        }

        // Add customer address - priority: selected PharmaFlow address > default PharmaFlow address > Whaticket contact address
        const selectedAddress = pharmaCustomer?.addresses?.length > 0
          ? pharmaCustomer.addresses.find(a => a.id === selectedAddressId) || pharmaCustomer.addresses[0]
          : null;

        if (selectedAddress?.street) {
          // Use PharmaFlow customer address
          orderData.customerAddress = {
            cep: selectedAddress.zipCode || selectedAddress.cep,
            street: selectedAddress.street,
            number: selectedAddress.number || selectedAddress.streetNumber,
            complement: selectedAddress.complement,
            neighborhood: selectedAddress.neighborhood,
            city: selectedAddress.city,
            state: selectedAddress.state,
            reference: selectedAddress.reference,
          };
        } else if (customerData?.street) {
          // Fallback to Whaticket contact address
          orderData.customerAddress = {
            cep: customerData.cep,
            street: customerData.street,
            number: customerData.streetNumber,
            complement: customerData.complement,
            neighborhood: customerData.neighborhood,
            city: customerData.city,
            state: customerData.state,
          };
        }


        const order = await pharmaApi.createOrder(orderData);

        toast.success(`Pedido #${order.orderNumber || order.id} finalizado! Resumo enviado e ticket encerrado.`);
        handleClearCart();
        setSalesDrawerOpen(false);
        setNotes("");
        setInternalNotes("");
        setDeliveryType("pickup");
        setChangeAmount(0);
      } catch (err) {
        console.error("Error creating order:", err);
        toastError(err);
      } finally {
        setIsSending(false);
      }
    }, [
      cart,
      isCustomerIdentified,
      selectedStore,
      ticketId,
      contactId,
      contact,
      contactLoading,
      contactPhone,
      customerData,
      paymentMethod,
      deliveryType,
      notes,
      internalNotes,
      recoveredQuoteId,
      settings,
      handleClearCart,
      pharmaCustomer,
      selectedAddressId,
      changeAmount,
    ]);

    // Show confirmation modal before finalizing
    const handleFinalizeSale = useCallback(() => {
      if (!cart || cart.length === 0) {
        toast.warning("Carrinho vazio!");
        return;
      }

      // Check if contact is still loading
      if (contactLoading) {
        toast.info("Carregando dados do contato. Aguarde um momento...");
        return;
      }

      // Check if contact is loaded before opening identification modal
      if (!contactId && !contact?.id) {
        toast.error("Contato não carregado. Recarregue a página e tente novamente.");
        console.error("[TicketPdvPanel] Contact not loaded:", { contactId, contact, contactLoading });
        return;
      }

      if (!isCustomerIdentified) {
        toast.warning("Preencha os dados do cliente antes de finalizar!");
        setCustomerModalOpen(true);
        return;
      }

      // Show confirmation modal
      setFinalizeConfirmOpen(true);
    }, [cart, isCustomerIdentified, contactLoading, contactId, contact]);

    // Handle finalize confirmation (always closes ticket)
    const handleFinalizeAndClose = useCallback(() => {
      handleFinalizeSaleInternal();
    }, [handleFinalizeSaleInternal]);

    // Send customer suggestions to chat
    const handleSendCustomerSuggestions = useCallback(
      async (products) => {
        if (!ticketUuid || !products || products.length === 0) return;

        setSendingSuggestions(true);
        try {
          const formatCurrency = (value) =>
            new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(value || 0);

          // Build suggestion message
          let message = "*Sugestoes especiais para voce:*\n\n";
          products.forEach((product, index) => {
            message += `${index + 1}. *${product.name}*\n`;
            message += `   ${formatCurrency(product.price)}`;
            if (product.reason) {
              message += ` - _${product.reason}_`;
            }
            message += "\n\n";
          });
          message += "_Gostaria de adicionar algum desses produtos ao seu pedido?_";

          await api.post(`/messages/${ticketUuid}`, {
            body: message,
            fromMe: true,
          });

          toast.success(`${products.length} sugestoes enviadas!`);
          setSuggestionsModalOpen(false);

          // If there was a pending finalize, proceed with it
          if (pendingFinalize) {
            setPendingFinalize(false);
            handleFinalizeSale();
          }
        } catch (err) {
          console.error("Error sending suggestions:", err);
          toast.error("Erro ao enviar sugestoes");
        } finally {
          setSendingSuggestions(false);
        }
      },
      [ticketUuid, pendingFinalize, handleFinalizeSale]
    );

    // Handle finalize with suggestions check
    const handleFinalizeWithSuggestions = useCallback(() => {
      // Check if there are customer suggestions to offer
      if (hasCustomerSuggestions && settings?.offerSuggestionsBeforeFinalize !== false) {
        setPendingFinalize(true);
        setSuggestionsModalOpen(true);
      } else {
        // No suggestions, proceed directly
        handleFinalizeSale();
      }
    }, [hasCustomerSuggestions, settings, handleFinalizeSale]);

    // Skip suggestions and finalize
    const handleSkipSuggestions = useCallback(() => {
      setSuggestionsModalOpen(false);
      setPendingFinalize(false);
      handleFinalizeSale();
    }, [handleFinalizeSale]);

    // Recover quote
    const handleRecoverQuote = useCallback(
      (quote, items) => {
        // Clear current cart
        clearCartPersisted();

        // Add items from quote
        items.forEach((item) => {
          const productSnapshot = JSON.parse(item.productSnapshot || "{}");
          addToCartPersisted({
            id: item.productId,
            name: productSnapshot.name || item.name,
            ean: productSnapshot.ean,
            activePrinciple: productSnapshot.activePrinciple,
            originalPrice: Number(item.originalPrice),
            unitPrice: Number(item.originalPrice),
            discount: Number(item.discount) || 0,
            discountPercent: Number(item.discountPercent) || 0,
            quantity: Number(item.quantity),
          });
        });

        setRecoveredQuoteId(quote.quoteNumber);
        setQuoteSearchOpen(false);
        setSalesDrawerOpen(true);
        toast.success(`Orçamento #${quote.quoteNumber} recuperado!`);
      },
      [clearCartPersisted, addToCartPersisted]
    );

    // Handle quote send from modal
    const handleQuoteSend = useCallback(
      async (message, targetTicketId) => {
        if (message && targetTicketId) {
          await api.post(`/messages/${targetTicketId}`, {
            body: message,
            fromMe: true,
          });
          toast.success("Orçamento enviado!");
        }
      },
      []
    );

    // Customer identification callback - receives updated contact from modal
    const handleCustomerIdentified = useCallback(
      (updatedContact) => {
        if (updatedContact) {
          setCustomerData({
            name: updatedContact.name || contactName,
            cpf: updatedContact.cpf,
            email: updatedContact.email,
            cep: updatedContact.zipCode || updatedContact.cep,
            street: updatedContact.street,
            streetNumber: updatedContact.streetNumber,
            complement: updatedContact.complement,
            neighborhood: updatedContact.neighborhood,
            city: updatedContact.city,
            state: updatedContact.state,
          });
          setCustomerModalOpen(false);

          // Notify parent to update contact state
          if (onContactUpdated) {
            onContactUpdated(updatedContact);
          }
        }
      },
      [contactName, onContactUpdated]
    );

    // Customer identification save
    const handleSaveCustomerData = useCallback(
      async (data) => {
        try {
          await api.put(`/contacts/${contactId}`, {
            cpf: data.cpf,
            email: data.email,
            zipCode: data.cep,
            street: data.street,
            streetNumber: data.streetNumber,
            complement: data.complement,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state,
          });

          setCustomerData({ ...data, streetNumber: data.streetNumber });
          setCustomerModalOpen(false);
          toast.success("Dados do cliente atualizados!");
        } catch (err) {
          toastError(err);
        }
      },
      [contactId]
    );

    // Keyboard navigation hook
    usePdvKeyboard({
      enabled: settings?.enableKeyboardShortcuts !== false,
      searchInputRef,
      products,
      cart,
      suggestions,
      paymentMethods: ['pix', 'credit', 'debit', 'money'],
      salesDrawerOpen,
      onAddToCart: handleAddToCart,
      onRemoveFromCart: (productId) => {
        const index = cart.findIndex(item => item.id === productId);
        if (index !== -1) handleRemoveItem(index);
      },
      onUpdateQuantity: (productId, delta) => {
        const index = cart.findIndex(item => item.id === productId);
        if (index !== -1) handleUpdateQuantity(index, delta);
      },
      onClearCart: handleClearCart,
      onSelectPayment: setPaymentMethod,
      onConfirmPayment: handleFinalizeSale,
      onCancel: () => setSalesDrawerOpen(false),
      onToggleCart: () => setSalesDrawerOpen(prev => !prev),
      onShowHelp: () => setKeyboardHelpOpen(true),
      onSaveQuote: handleSaveQuote,
      onAddSuggestion: handleAddToCart,
    });

    // Focus search on mount
    useEffect(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, []);

    // Register PDV actions for header buttons
    useEffect(() => {
      registerPdvActions({
        onCartClick: () => setSalesDrawerOpen(true),
        onHelpClick: () => setKeyboardHelpOpen(true),
        onQuotesClick: () => setQuoteSearchOpen(true),
      });
    }, [registerPdvActions]);

    // Update cart info for header badge
    useEffect(() => {
      updateCartInfo(totals.itemCount, totals.total);
    }, [totals.itemCount, totals.total, updateCartInfo]);


    return (
      <Box className={classes.root} ref={internalContainerRef}>
        {/* Header removido - usando o do TicketsModern */}

        {/* Store Selector moved to header */}

        {/* Customer Summary */}
        <PdvCustomerSummary
          contact={customerData}
          contactLoading={loading}
          customerStats={customerStats}
          statsLoading={statsLoading}
          ticketId={ticketId}
          onIdentifyClick={() => setCustomerProfileOpen(true)}
          onEditClick={() => setCustomerModalOpen(true)}
        />

        {/* Customer Suggestions Notification - between customer card and search */}
        {hasCustomerSuggestions && !salesDrawerOpen && (
          <PdvSuggestionsNotification
            suggestions={customerSuggestions}
            loading={customerSuggestionsLoading}
            customerName={customerData?.name || contactName}
            onAddProduct={handleAddToCart}
            onSendProduct={handleSendProduct}
            onSendAll={() => handleSendCustomerSuggestions(customerSuggestions)}
            onDismiss={dismissCustomerSuggestion}
            onDismissAll={dismissAllCustomerSuggestions}
          />
        )}

        {/* Search Bar */}
        <PdvSearchBar
          inputRef={searchInputRef}
          value={searchQuery}
          onChange={setSearchQuery}
          loading={searching}
          placeholder="Buscar Produtos"
        />

        {/* Product List */}
        <PdvProductList
          listRef={productListRef}
          products={products}
          selectedIndex={selectedProductIndex}
          onSelectProduct={setSelectedProductIndex}
          onAddProduct={handleAddToCart}
          onSendProduct={handleSendProduct}
          sendingProductId={sendingProductId}
          isLoading={searching}
          hasSearchQuery={!!searchQuery}
        />

        {/* Suggestions Panel - aparece abaixo dos resultados de busca */}
        {(sameFormula?.products?.length > 0 || relatedProducts?.products?.length > 0) && (
          <PdvSuggestionsPanel
            sameFormula={sameFormula}
            relatedProducts={relatedProducts}
            onAddProduct={handleAddToCart}
            onClose={clearSuggestions}
          />
        )}

        {/* Unified Sales Drawer - Cart + Checkout with smooth transitions */}
        <PdvSalesDrawer
          open={salesDrawerOpen}
          onClose={() => setSalesDrawerOpen(false)}
          // Cart props
          cart={cart}
          selectedIndex={selectedCartIndex}
          onSelectItem={setSelectedCartIndex}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onApplyDiscount={handleOpenDiscountModal}
          onClearCart={handleClearCart}
          totals={totals}
          listRef={cartListRef}
          recoveredQuoteId={recoveredQuoteId}
          // Checkout props
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
          changeAmount={changeAmount}
          onChangeAmountChange={setChangeAmount}
          deliveryType={deliveryType}
          onDeliveryTypeChange={setDeliveryType}
          deliveryFee={Number(settings?.deliveryBaseFee) || 0}
          isFreeDelivery={Number(settings?.deliveryFreeAbove) > 0 && totals.total >= Number(settings.deliveryFreeAbove)}
          notes={notes}
          onNotesChange={setNotes}
          internalNotes={internalNotes}
          onInternalNotesChange={setInternalNotes}
          isCustomerIdentified={isCustomerIdentified}
          customerAddress={
            // Priority: PharmaFlow selected address > PharmaFlow default address > Whaticket contact address
            pharmaCustomer?.addresses?.length > 0
              ? pharmaCustomer.addresses.find(a => a.id === selectedAddressId) || pharmaCustomer.addresses[0]
              : customerData ? {
                  id: 'whaticket',
                  label: 'Endereço Principal',
                  street: customerData.street,
                  number: customerData.streetNumber,
                  complement: customerData.complement,
                  neighborhood: customerData.neighborhood,
                  city: customerData.city,
                  state: customerData.state,
                  zipCode: customerData.cep,
                } : null
          }
          customerAddresses={pharmaCustomer?.addresses || []}
          selectedAddressId={selectedAddressId}
          onAddressChange={setSelectedAddressId}
          onIdentifyCustomer={() => setCustomerModalOpen(true)}
          onFinalizeSale={handleFinalizeWithSuggestions}
          onSaveQuote={handleSaveQuote}
          isSending={isSending}
          isSavingQuote={isSavingQuote}
          containerRef={containerRef}
        />

        {/* Customer Identification Modal */}
        <CustomerIdentificationModal
          open={customerModalOpen}
          onClose={() => setCustomerModalOpen(false)}
          contact={contact}
          contactId={contactId}
          onIdentified={handleCustomerIdentified}
        />

        {/* Customer Profile Modal - Full profile with stats, history, suggestions */}
        <CustomerProfileModal
          open={customerProfileOpen}
          onClose={() => setCustomerProfileOpen(false)}
          contact={contact}
          storeId={selectedStore?.id}
          onAddToCart={handleAddToCart}
          onCustomerUpdate={fetchPharmaCustomer}
        />

        {/* Quote Search Modal */}
        <QuoteSearchModal
          open={quoteSearchOpen}
          onClose={() => setQuoteSearchOpen(false)}
          onRecover={handleRecoverQuote}
          onSend={handleQuoteSend}
          ticketId={ticketId}
          ticketUuid={ticketUuid}
          contactId={contactId}
          contactPhone={contactPhone}
        />

        {/* Keyboard Shortcuts Help */}
        <PdvKeyboardShortcuts
          open={keyboardHelpOpen}
          onClose={() => setKeyboardHelpOpen(false)}
        />

        {/* Discount Modal */}
        <PdvDiscountModal
          open={discountModalOpen}
          onClose={() => {
            setDiscountModalOpen(false);
            setDiscountTarget(null);
          }}
          item={discountTarget !== null ? cart[discountTarget] : null}
          onApplyDiscount={(discountData) => {
            if (discountTarget !== null) {
              handleApplyDiscount(discountTarget, discountData.discountValue, discountData.discountPercent);
            }
            setDiscountModalOpen(false);
            setDiscountTarget(null);
          }}
          settings={settings}
        />

        {/* Send Suggestions Modal - before finalizing sale */}
        <PdvSendSuggestionsModal
          open={suggestionsModalOpen}
          onClose={() => {
            setSuggestionsModalOpen(false);
            setPendingFinalize(false);
          }}
          suggestions={customerSuggestions}
          customerName={customerData?.name || contactName}
          onSend={handleSendCustomerSuggestions}
          onSkip={handleSkipSuggestions}
          sending={sendingSuggestions}
        />

        {/* Finalize Sale Confirmation Modal */}
        <PdvFinalizeConfirmModal
          open={finalizeConfirmOpen}
          onClose={() => setFinalizeConfirmOpen(false)}
          onConfirm={handleFinalizeAndClose}
          loading={isSending}
          orderTotal={totals.total}
        />

        {/* Floating Cart Button */}
        <button
          className={`${classes.floatingCartButton} ${totals.itemCount > 0 ? classes.floatingCartButtonWithItems : ''}`}
          onClick={() => setSalesDrawerOpen(true)}
                  >
          <ShoppingCartIcon className={classes.floatingCartButtonIcon} />
          {totals.itemCount > 0 && (
            <>
              <span className={classes.floatingCartButtonBadge}>{totals.itemCount}</span>
              <span className={classes.floatingCartButtonTotal}>
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totals.total)}
              </span>
            </>
          )}
        </button>
      </Box>
    );
  };

  export default TicketPdvPanel;
